/**
 * ثبت فاکتور — ویزارد دسته‌محور (خرید نخ / بافندگی / رنگرزی)
 * URL منبع حقیقت؛ remount فرم با key؛ draft جدا per category؛ Navigation Guard
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Card, Button, Space, Tag, Select, Form, Input, InputNumber, message,
  Collapse, Typography, Grid, Checkbox, Segmented, Steps, List,
} from "antd";
import {
  ArrowRightOutlined, CheckOutlined, ThunderboltOutlined, FormOutlined,
  SaveOutlined, HistoryOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { scCreateEvent, scGetParties, scGetItems } from "../../api/client";
import { JalaliDateInput, todayISO } from "../../utils/dateUtils";
import { useAuth } from "../../context/AuthContext";
import { fmtRial } from "./scConstants";
import { getTemplate } from "./movementTemplates";
import {
  enabledCategories, getCategory, operationsFor, categoryIcon, newInvoicePath,
} from "./supplyCategories";
import { loadDraft, saveDraft, clearDraft, listDrafts } from "../../utils/invoiceDrafts";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import useColorOptions from "../../hooks/useColorOptions";
import { CreatableSelect } from "../../components/EditableTable";
import PartyPicker from "./PartyPicker";

const { Text, Title, Paragraph } = Typography;
const MODE_KEY = "gx-invoice-mode";

function InvoiceFormStep({ category, eventType, parties, onPartyCreated, items, mode, onBack, onSubmitted }) {
  const { can } = useAuth();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const { colorOptions, registerColor } = useColorOptions();
  const snapshotRef = useRef({});

  // بارگذاری اولیه / draft
  useEffect(() => {
    const draft = loadDraft(category);
    form.resetFields();
    if (draft && draft.eventType === eventType && draft.values) {
      form.setFieldsValue({ ...draft.values, event_type: eventType });
    } else {
      form.setFieldsValue({ event_type: eventType, event_date: todayISO() });
    }
    snapshotRef.current = { ...form.getFieldsValue(true) };
  }, [category, eventType, form]);

  const persistDraft = useCallback(() => {
    const values = form.getFieldsValue(true);
    saveDraft(category, { values, eventType });
    snapshotRef.current = { ...values };
    message.success("پیش‌نویس محلی ذخیره شد");
    return true;
  }, [category, eventType, form]);

  const discardDraft = useCallback(() => {
    clearDraft(category);
    form.resetFields();
    form.setFieldsValue({ event_type: eventType, event_date: todayISO() });
    snapshotRef.current = { ...form.getFieldsValue(true) };
  }, [category, eventType, form]);

  const submitFinal = useCallback(async () => {
    if (!can("sc.event.create") && !can("supply_chain.create")) return false;
    try {
      const v = await form.validateFields();
      setSaving(true);
      await scCreateEvent({ ...v, event_type: eventType });
      clearDraft(category);
      snapshotRef.current = { ...form.getFieldsValue(true) };
      message.success("فاکتور به‌صورت پیش‌نویس ثبت شد");
      return true;
    } catch (e) {
      if (e?.errorFields) return false;
      message.error(e?.response?.data?.detail || "خطا در ثبت");
      return false;
    } finally {
      setSaving(false);
    }
  }, [can, form, eventType, category]);

  const canFinalize = useCallback(() => {
    if (!can("sc.event.create") && !can("supply_chain.create")) {
      return { ok: false, reason: "مجوز ثبت فاکتور ندارید" };
    }
    if (!eventType) return { ok: false, reason: "نوع عملیات انتخاب نشده" };
    return { ok: true, reason: "" };
  }, [can, eventType]);

  useUnsavedChangesGuard({
    enabled: true,
    getValues: () => form.getFieldsValue(true),
    snapshotRef,
    canFinalize,
    onSaveDraft: persistDraft,
    onFinalize: submitFinal,
    onDiscard: discardDraft,
  });

  const submit = async () => {
    const ok = await submitFinal();
    if (ok && onSubmitted) onSubmitted();
  };

  return (
    <InvoiceFormInner
      category={category}
      eventType={eventType}
      form={form}
      parties={parties}
      onPartyCreated={onPartyCreated}
      items={items}
      mode={mode}
      saving={saving}
      snapshotRef={snapshotRef}
      onBack={onBack}
      onSubmit={submit}
      onSaveDraftOnly={persistDraft}
    />
  );
}

function InvoiceFormInner({
  category, eventType, form, parties, onPartyCreated, items, mode, saving,
  onBack, onSubmit, onSaveDraftOnly, snapshotRef,
}) {
  /* 🐞 `colorOptions`/`registerColor` در کامپوننتِ **بیرونی** ساخته می‌شدند و
     اینجا (جایی که واقعاً استفاده می‌شوند) خارج از دامنه بودند — یعنی به‌محضِ
     رندرِ فیلدِ «رنگ»، ReferenceError و صفحهٔ خطا. کاتالوگِ رنگ خودش کش دارد،
     پس صدازدنِ دوباره‌اش هزینه‌ای ندارد. */
  const { colorOptions, registerColor } = useColorOptions();
  const wWeight = Form.useWatch("weight_kg", form);
  const wPrice = Form.useWatch("unit_price", form);
  const wOverride = Form.useWatch("manual_total_override", form);
  const wTotal = Form.useWatch("total_amount", form);
  const tpl = getTemplate(eventType);
  const isQuick = mode === "quick";
  const partyOfType = (ptype) => parties.find((p) => p.party_type === ptype);
  const fixedSrc = tpl.fixedSourceType ? partyOfType(tpl.fixedSourceType) : null;
  const fixedDst = tpl.fixedDestType ? partyOfType(tpl.fixedDestType) : null;

  useEffect(() => {
    if (!wOverride && wWeight && wPrice) {
      form.setFieldsValue({ total_amount: Math.round(Number(wWeight) * Number(wPrice)) });
    }
  }, [wWeight, wPrice, wOverride, form]);

  // baseline بعد از mount / بارگذاری draft
  useEffect(() => {
    snapshotRef.current = { ...(form.getFieldsValue(true) || {}) };
  }, [category, eventType, form, snapshotRef]);

  return (
    <Form form={form} layout="vertical" style={{ direction: "rtl", maxWidth: 640 }}
      onValuesChange={() => { /* dirty via snapshot */ }}>
      <Form.Item name="event_type" hidden><Input /></Form.Item>
      <Form.Item name="event_date" label="تاریخ" rules={[{ required: true, message: "تاریخ الزامی است" }]}>
        <JalaliDateInput />
      </Form.Item>

      {fixedSrc && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">مبدأ (ثابت): </Text>
          <Tag color="blue">{fixedSrc.name}</Tag>
        </div>
      )}
      {[tpl.sourceSelect, tpl.destSelect].filter(Boolean).map((cfg) => (
        <Form.Item key={cfg.name} name={cfg.name} label={cfg.label}
          rules={[{ required: (tpl.required || []).includes(cfg.name), message: "الزامی" }]}>
          <PartyPicker types={cfg.types} parties={parties} onCreated={onPartyCreated} defaultHint={cfg.defaultHint} />
        </Form.Item>
      ))}
      {fixedDst && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">مقصد (ثابت): </Text>
          <Tag color="green">{fixedDst.name}</Tag>
        </div>
      )}

      {(tpl.main || []).filter((f) => !["source_party_id", "destination_party_id", "current_holder_party_id"].includes(f)).map((name) => {
        if (name === "weight_kg") {
          return (
            <Form.Item key={name} name="weight_kg" label="وزن (کیلوگرم)"
              rules={[{ required: true, message: "وزن الزامی است" }]}>
              <InputNumber min={0.001} style={{ width: "100%" }} size="large" />
            </Form.Item>
          );
        }
        if (name === "item_type_id") {
          return (
            <Form.Item key={name} name="item_type_id" label="نوع کالا">
              <Select allowClear size="large" options={items.map((i) => ({ value: i.id, label: i.name }))} />
            </Form.Item>
          );
        }
        if (name === "fabric_type") {
          return <Form.Item key={name} name="fabric_type" label="نوع پارچه"><Input size="large" /></Form.Item>;
        }
        if (name === "color") {
          return (
            // رنگِ تازه با Enter همان‌جا در کاتالوگِ مشترک ثبت می‌شود
            <Form.Item key={name} name="color" label="رنگ">
              <CreatableSelect options={colorOptions} onCreate={registerColor} size="large" />
            </Form.Item>
          );
        }
        return null;
      })}

      {!isQuick && (
        <Collapse style={{ marginBottom: 12 }} items={[
          {
            key: "docs",
            label: "شماره‌ها و توضیحات",
            children: (
              <>
                <Form.Item name="external_invoice_number" label="شماره فاکتور"><Input size="large" /></Form.Item>
                <Form.Item name="order_code" label="شماره سفارش"><Input size="large" /></Form.Item>
                <Form.Item name="external_document_number" label="شماره سند"><Input size="large" /></Form.Item>
                <Form.Item name="permit_number" label="شماره مجوز"><Input size="large" /></Form.Item>
                <Form.Item name="roll_count" label="تعداد طاقه"><InputNumber min={0} style={{ width: "100%" }} size="large" /></Form.Item>
                <Form.Item name="carton_count" label="تعداد کارتن"><InputNumber min={0} style={{ width: "100%" }} size="large" /></Form.Item>
                <Form.Item name="pallet_count" label="تعداد پالت"><InputNumber min={0} style={{ width: "100%" }} size="large" /></Form.Item>
                <Form.Item name="notes" label="توضیحات"><Input.TextArea rows={2} /></Form.Item>
              </>
            ),
          },
          {
            key: "acc",
            label: "اطلاعات مالی",
            children: (
              <>
                <Form.Item name="unit_price" label="فی (ریال)">
                  <InputNumber min={0} style={{ width: "100%" }} size="large"
                    formatter={(x) => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} />
                </Form.Item>
                <Form.Item name="manual_total_override" valuePropName="checked">
                  <Checkbox>ویرایش دستی مبلغ</Checkbox>
                </Form.Item>
                <Form.Item name="total_amount" label="مبلغ کل (ریال)">
                  <InputNumber min={0} disabled={!wOverride} style={{ width: "100%" }} size="large"
                    formatter={(x) => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} />
                </Form.Item>
                <div style={{ fontWeight: 700, color: "#107C41" }}>جمع: {fmtRial(wTotal)}</div>
              </>
            ),
          },
        ]} />
      )}

      {isQuick && (
        <Space wrap style={{ width: "100%" }}>
          <Form.Item name="external_invoice_number" label="شماره فاکتور / سند" style={{ minWidth: 200 }}>
            <Input size="large" />
          </Form.Item>
          <Form.Item name="unit_price" label="فی (اختیاری)" style={{ minWidth: 160 }}>
            <InputNumber min={0} style={{ width: "100%" }} size="large"
              formatter={(x) => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} />
          </Form.Item>
        </Space>
      )}

      <Space style={{ marginTop: 8 }} wrap>
        <Button onClick={onBack}>بازگشت</Button>
        <Button icon={<SaveOutlined />} onClick={onSaveDraftOnly}>ذخیره پیش‌نویس محلی</Button>
        <Button type="primary" icon={<CheckOutlined />} loading={saving} onClick={onSubmit}>
          ثبت پیش‌نویس
        </Button>
      </Space>
    </Form>
  );
}

export default function InvoiceCreatePage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const isMobile = !Grid.useBreakpoint().md;
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [mode, setMode] = useState(() => localStorage.getItem(MODE_KEY) || "quick");
  const [draftTick, setDraftTick] = useState(0);

  const category = sp.get("category") || null;
  const eventType = sp.get("type") || null;

  useEffect(() => {
    scGetParties().then((r) => setParties(r.data)).catch(() => {});
    scGetItems().then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = sp.get("type");
    const c = sp.get("category");
    if (t && !c) {
      const cat = enabledCategories().find((x) => (x.operations || []).some((o) => o.event_type === t));
      if (cat) setSp({ category: cat.key, type: t }, { replace: true });
    }
  }, [sp, setSp]);

  const cats = useMemo(
    () => enabledCategories().filter((c) => !c.perm || can(c.perm) || can("sc.event.create")),
    [can],
  );
  const ops = category ? operationsFor(category) : [];
  const drafts = useMemo(() => listDrafts(cats.map((c) => c.key)), [cats, draftTick, category, eventType]);

  const pickCategory = (key) => setSp({ category: key }, { replace: true });
  const pickOp = (et) => setSp({ category: category || "", type: et }, { replace: true });
  const setModePersist = (m) => {
    setMode(m);
    localStorage.setItem(MODE_KEY, m);
  };

  const step = !category ? 0 : (!eventType ? 1 : 2);

  if (!can("sc.event.create") && !can("supply_chain.create")) {
    return <Card><Text type="danger">دسترسی ثبت فاکتور ندارید.</Text></Card>;
  }

  const tpl = getTemplate(eventType);
  const formKey = `invoice-form-${category || "none"}-${eventType || "pick"}`;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>ثبت فاکتور</Title>
        {step > 0 && (
          <Segmented
            value={mode}
            onChange={setModePersist}
            options={[
              { value: "quick", icon: <ThunderboltOutlined />, label: "ثبت سریع" },
              { value: "full", icon: <FormOutlined />, label: "ثبت کامل" },
            ]}
          />
        )}
      </div>

      <Steps
        size="small"
        current={step}
        style={{ marginBottom: 20, maxWidth: 560 }}
        items={[
          { title: "دسته" },
          { title: "عملیات" },
          { title: "اطلاعات" },
        ]}
      />

      {step === 0 && (
        <>
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
            gap: 14,
          }}>
            {cats.map((c) => (
              <Card
                key={c.key}
                hoverable
                onClick={() => pickCategory(c.key)}
                style={{ cursor: "pointer", borderColor: "var(--border)" }}
              >
                <Space align="start" size={12}>
                  <span style={{ fontSize: 28, color: "var(--brand, #1F4E79)" }}>{categoryIcon(c.key)}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{c.label}</div>
                    <Paragraph type="secondary" style={{ margin: "6px 0 0", fontSize: 13 }}>{c.blurb}</Paragraph>
                  </div>
                </Space>
              </Card>
            ))}
          </div>

          <Button style={{ marginTop: 16 }} onClick={() => navigate("/supply-chain/import")}>
            ایمپورت فایل
          </Button>

          {drafts.length > 0 && (
            <Card
              title={<Space><HistoryOutlined /> پیش‌نویس‌های اخیر</Space>}
              style={{ marginTop: 20 }}
              size="small"
            >
              <List
                dataSource={drafts}
                renderItem={(d) => (
                  <List.Item
                    actions={[
                      <Button type="link" key="c" onClick={() => {
                        const q = { category: d.category };
                        if (d.eventType) q.type = d.eventType;
                        setSp(q, { replace: true });
                      }}>ادامه</Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={getCategory(d.category)?.label || d.category}
                      description={d.updatedAt ? new Date(d.updatedAt).toLocaleString("fa-IR") : ""}
                    />
                  </List.Item>
                )}
              />
            </Card>
          )}
        </>
      )}

      {step === 1 && (
        <Card>
          <Space style={{ marginBottom: 12 }}>
            <Button type="link" icon={<ArrowRightOutlined />} onClick={() => setSp({}, { replace: true })}>
              تغییر دسته
            </Button>
            <Tag color="blue">{getCategory(category)?.label}</Tag>
          </Space>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 420 }}>
            {ops.map((o) => (
              <Button key={o.event_type} size="large" type="default" block
                onClick={() => pickOp(o.event_type)}
                style={{ textAlign: "right", height: 48 }}>
                {o.label}
              </Button>
            ))}
            {!ops.length && <Text type="secondary">عملیاتی برای این دسته تعریف نشده.</Text>}
          </div>
          {loadDraft(category) && (
            <Button style={{ marginTop: 12 }} type="dashed" block
              onClick={() => {
                const d = loadDraft(category);
                if (d?.eventType) setSp({ category, type: d.eventType }, { replace: true });
              }}>
              ادامه پیش‌نویس این دسته
            </Button>
          )}
          <Button style={{ marginTop: 16 }} onClick={() => navigate("/supply-chain/import")}>
            یا ایمپورت از فایل
          </Button>
        </Card>
      )}

      {step === 2 && (
        <Card title={tpl.label || "فرم فاکتور"}>
          <InvoiceFormStep
            key={formKey}
            category={category}
            eventType={eventType}
            parties={parties}
            onPartyCreated={(p) => setParties((ps) => [...ps, p])}
            items={items}
            mode={mode}
            onBack={() => setSp(category ? { category } : {}, { replace: true })}
            onSubmitted={() => {
              setDraftTick((t) => t + 1);
              navigate("/supply-chain/events");
            }}
          />
        </Card>
      )}
    </div>
  );
}

export { newInvoicePath };
